import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import type {EventWizardOrganisersProps, OrganiserDetails} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.types';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {getOrganiserProfilePicture} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.helper';
import styles from './event-wizard-organiser-options.module.scss';

export function EventWizardOrganiserOptions({organisersDetails, onClickOption}: EventWizardOrganisersProps): ReactElement<any> {
  const carouselRef = useRef<HTMLDivElement>(null);
  const nextOverflowRef = useRef<HTMLDivElement>(null);
  const backOverflowRef = useRef<HTMLDivElement>(null);
  const hideStyle = '_hidden';

  useEffect(() => {
    updateOverflowVisibility();

    if (carouselRef.current) {
      carouselRef.current.addEventListener('scroll', updateOverflowVisibility);
    }
  }, []);

  const updateOverflowVisibility = (): void => {
    if (carouselRef.current) {
      const {scrollWidth, clientWidth, scrollLeft} = carouselRef.current;

      if (scrollWidth <= clientWidth) {
        backOverflowRef.current?.classList.add(hideStyle);
        nextOverflowRef.current?.classList.add(hideStyle);
        return;
      }

      if (scrollLeft === 0) {
        backOverflowRef.current?.classList.add(hideStyle);
        nextOverflowRef.current?.classList.remove(hideStyle);
        return;
      }

      if (scrollLeft === scrollWidth - clientWidth) {
        backOverflowRef.current?.classList.remove(hideStyle);
        nextOverflowRef.current?.classList.add(hideStyle);
        return;
      }
      backOverflowRef.current?.classList.remove(hideStyle);
      nextOverflowRef.current?.classList.remove(hideStyle);
    }
  };

  const scrollRight = (): void => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: 200,
        behavior: 'smooth',
      });
    }
  };

  const scrollLeft = (): void => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: -200,
        behavior: 'smooth',
      });
    }
  };

  const onClickOrganiserOption = (option: OrganiserDetails): void => {
    onClickOption(option);
  };

  const getOrganiserPreFillCard = (organiserDetails: OrganiserDetails, id: number): ReactElement<any> => {
    return (
      <ClickableDiv
        key={`${id}_${organiserDetails.name}`}
        onClick={() => {
          return onClickOrganiserOption(organiserDetails);
        }}
        className={`${styles.presetOrganiserContainer} flex-row-align-center border-s-dark radius-8 spacing-p-2 _fit-width _cursor-pointer`}
      >
        <div className="spacing-m-r-2 _position-relative">
          <div className={`${styles.profileImgContainer} border-s-standard radius-round _overflow-hidden`}>
            <img className="_full-width _full-height" src={getOrganiserProfilePicture(organiserDetails)} alt="" />
          </div>
          {organiserDetails.isFromBusinessProfile ? (
            <div className={`${styles.presetImgIconContainer} radius-round flex-row-align-center justify-content-center _position-absolute`}>
              <i className={`${styles.presetImgIcon} spacing-m-0 icon-business`} />
            </div>
          ) : undefined}
        </div>
        <div className="flex-column-align-start">
          <Text className={`${styles.presetText} -truncated-text`} val={organiserDetails.name} size={TextSize.XXSMALL} bold />
          <Text className={`${styles.presetText} content-sub-text -truncated-text`} val={organiserDetails.email} size={TextSize.XXSMALL} />
        </div>
      </ClickableDiv>
    );
  };

  const getAllOrganiserPreFillCard = (): Array<ReactElement<any>> => {
    const preFillCards: Array<ReactElement<any>> = [];

    organisersDetails.forEach((preFillOption, i) => {
      preFillCards.push(getOrganiserPreFillCard(preFillOption, i));
    });
    return preFillCards;
  };

  return (
    <div className={styles.carouselContainer}>
      <div ref={carouselRef} className={`${styles.presetOrganisersContainer} flex-row-align-center`}>
        {getAllOrganiserPreFillCard()}
      </div>
      <ClickableDiv onClick={scrollRight} ref={nextOverflowRef} className="_hidden _cursor-pointer">
        <Icon className={`${styles.nextScrollIcon} ${styles.scrollIcon}`} icon="icon-caret-right" size={IconSize.SIZE_ICON_16} type={IconType.SHADOW} shape={IconShape.CIRCLE} />
        <div className={`${styles.fade} ${styles.nextFade}`} />
      </ClickableDiv>
      <ClickableDiv onClick={scrollLeft} ref={backOverflowRef} className="_hidden _cursor-pointer">
        <Icon className={`${styles.backScrollIcon} ${styles.scrollIcon}`} icon="icon-caret-left" size={IconSize.SIZE_ICON_16} type={IconType.SHADOW} shape={IconShape.CIRCLE} />
        <div className={`${styles.fade} ${styles.backFade}`} />
      </ClickableDiv>
    </div>
  );
}
